'use client'

import { content } from '@/config/content'
import { useSiteContent } from '@/lib/useSiteContent'
import { useSiteSettings } from '@/lib/useSiteSettings'

export default function About() {
  // DB-driven: site_content section='about' (about-main + about-booking-note)
  // with fallback to config/content.js when DB empty.
  const { items: aboutRows } = useSiteContent('about')
  const { settings: brand } = useSiteSettings('brand')
  const { settings: pricing } = useSiteSettings('pricing')

  // Phase 2.6 — render every visible about row in sort_order so admin can
  // add new paragraphs without code changes. Legacy rows 'about-main' and
  // 'about-booking-note' are still respected by matching their id, and
  // additional rows (e.g. 'about-extra-1') appear automatically.
  //
  // Special id meanings (preserved for backward compat):
  //   - 'about-booking-note' → rendered inside the Contact info grid, not bio.
  //   - All other visible rows → rendered as bio paragraphs in sort_order.
  const bookingNoteRow = aboutRows.find((r) => r.id === 'about-booking-note')
  const bioRows = aboutRows.filter((r) => r.id !== 'about-booking-note')

  const bioParagraphs = bioRows.length > 0
    ? bioRows.map((r) => ({ id: r.id, body: r.body || '' })).filter((r) => r.body)
    : content.about.bio.split('\n').map((line, i) => ({ id: `fallback-${i}`, body: line }))

  // First bio paragraph still uses the drop-cap accent — keep existing visual.
  const bioLines = bioParagraphs.map((p) => p.body)
  const bookingNote = bookingNoteRow?.body || content.about.bookingNote

  const brandIg = brand.brand_ig || content.brand.ig
  const brandIgUrl = brand.brand_ig_url || content.brand.igUrl

  // Phase 3.2 — Contact / Pricing labels CMS-driven (M5)
  const contactLabel = brand.about_contact_label || 'Contact'
  const pricingLabel = brand.about_pricing_label || 'Pricing'

  const flashPrice = pricing.price_flash
    ? `NT$${Number(pricing.price_flash).toLocaleString('zh-TW')}`
    : content.pricing.flash
  const customPrice = pricing.price_custom_from
    ? `NT$${Number(pricing.price_custom_from).toLocaleString('zh-TW')} 起`
    : content.pricing.custom

  return (
    <section style={{ padding: '0 var(--spacing-pagePadding) 48px' }}>
      <div className="max-w-lg mx-auto">
        {/* Bio with drop cap on first paragraph — Phase 2.6 dynamic render */}
        {bioLines.map((line, i) => (
          <p
            key={bioParagraphs[i]?.id ?? i}
            className="text-sm leading-7 mb-3 stagger-fade-in"
            style={{
              color: 'var(--color-textSecondary)',
              '--stagger-delay': `${i * 0.1}s`,
              ...(i === 0 ? { lineHeight: '1.8' } : {}),
            }}
          >
            {i === 0 && line.length > 0 ? (
              <>
                <span
                  className="float-left text-3xl font-bold mr-2"
                  style={{
                    fontFamily: 'var(--font-accent)',
                    color: 'var(--color-accent)',
                    lineHeight: '1',
                    marginTop: '4px',
                    opacity: 0.7,
                  }}
                >
                  {line[0]}
                </span>
                {line.slice(1)}
              </>
            ) : line}
          </p>
        ))}

        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-2 my-10">
          <span
            style={{ width: '24px', height: '1px', background: 'var(--color-text)', opacity: 0.15 }}
          />
          <span
            style={{
              width: '4px', height: '4px', borderRadius: '50%',
              background: 'var(--color-accent)', opacity: 0.4,
            }}
          />
          <span
            style={{ width: '24px', height: '1px', background: 'var(--color-text)', opacity: 0.15 }}
          />
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div className="stagger-fade-in" style={{ '--stagger-delay': '0.3s' }}>
            <span className="accent-label text-xs tracking-widest uppercase block mb-3" style={{ color: 'var(--color-textMuted)' }}>
              {contactLabel}
            </span>
            <p className="text-sm mb-1" style={{ color: 'var(--color-textSecondary)' }}>
              {bookingNote}
            </p>
            <a
              href={brandIgUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium inline-block mt-1 transition-opacity hover:opacity-70"
              style={{ color: 'var(--color-text)' }}
            >
              @{brandIg}
            </a>
          </div>

          <div className="stagger-fade-in" style={{ '--stagger-delay': '0.4s' }}>
            <span className="accent-label text-xs tracking-widest uppercase block mb-3" style={{ color: 'var(--color-textMuted)' }}>
              {pricingLabel}
            </span>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  {flashPrice}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-textMuted)' }}>
                  {content.pricing.flashNote}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  {customPrice}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-textMuted)' }}>
                  {content.pricing.customNote}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
